<template>
  <auth-layout pageClass="privacy-page">
    <div class="row d-flex justify-content-center align-items-center">
      <div class="col-lg-10 col-md-11 col-sm-12">
        <card>
          <div class="card-header" slot="header">
            <h3
              class="card-title text-center"
              style="color: #d9d9d9; font-weight: bold"
            >
              Privacy Policy
            </h3>
          </div>
          <div class="card-body privacy-content">
            <p class="subtitle">Privacy Policy for Oppo</p>
            <p class="last-updated">Last updated: November 25, 2025</p>

            <p>
              The Oppo ("we," "our," or "us") operates the Oppo mobile
              application ("the App"). This Privacy Policy explains how we
              collect, use, and protect information within the App. The App is
              intended solely for authorized employees of The Oppo for internal
              business purposes.
            </p>

            <h4>1. Purpose of the App</h4>
            <p>Oppo is used by employees to:</p>
            <ul>
              <li>Log in to their employee account</li>
              <li>Clock in and clock out of scheduled shifts with clients</li>
              <li>
                Verify their physical presence at the work location through
                GPS/geofencing
              </li>
            </ul>
            <p>
              The App is not available to the public and is not intended for
              consumer use.
            </p>

            <h4>2. Information We Collect</h4>

            <h5>a. Employee Account Information</h5>
            <p>Employees must log in to use the App. We collect and process:</p>
            <ul>
              <li>Employee name</li>
              <li>Employee ID or username</li>
              <li>Company-issued email (if applicable)</li>
              <li>Authentication credentials</li>
            </ul>

            <h5>b. Shift and Work-Related Information</h5>
            <p>During normal use, employees enter or generate:</p>
            <ul>
              <li>Clock-in and clock-out times</li>
              <li>Client or shift assignment identifiers</li>
              <li>Notes or job-related inputs (if applicable)</li>
            </ul>

            <h5>c. GPS Location Data (Required for Clock-In Verification)</h5>
            <p>When employees clock in, the App collects:</p>
            <ul>
              <li>The employee's GPS location</li>
              <li>
                Geofence confirmation (verifying the employee is at the
                authorized location)
              </li>
            </ul>
            <p>
              Location data is collected only at clock-in and clock-out as
              required for verifying attendance. We do not track employee
              location continuously.
            </p>

            <h5>d. Automatically Collected Technical Data</h5>
            <p>
              For performance, security, and troubleshooting, we may collect:
            </p>
            <ul>
              <li>Device type and operating system</li>
              <li>App usage logs</li>
              <li>Error/crash diagnostics</li>
              <li>Authentication activity</li>
            </ul>

            <h4>3. How We Use the Information</h4>
            <p>
              We use the collected information solely for internal business
              purposes, including:
            </p>
            <ul>
              <li>
                Verifying employee attendance and location at the start of
                shifts
              </li>
              <li>Managing payroll and timekeeping</li>
              <li>Managing employee schedules and client visits</li>
              <li>Ensuring compliance with company policies</li>
              <li>Securing and improving the App</li>
            </ul>
            <p>
              We do not use any data for marketing, advertising, or external
              profiling.
            </p>

            <h4>4. Data Sharing</h4>
            <p>We may share information only with:</p>
            <ul>
              <li>
                Internal departments of The Oppo (HR, management, payroll, IT)
              </li>
              <li>
                Service providers that support the App (hosting, authentication,
                analytics)
              </li>
            </ul>
            <p>
              These service providers are required to protect the data and may
              not use it for any purpose other than supporting The Oppo.
            </p>
            <p><strong>We never sell employee information.</strong></p>

            <h4>5. Data Security</h4>
            <p>
              We implement reasonable and industry-standard security measures,
              including:
            </p>
            <ul>
              <li>Encrypted data transmission</li>
              <li>Access control and authentication</li>
              <li>Secure cloud storage</li>
              <li>Monitoring for unauthorized access</li>
            </ul>
            <p>
              Employees are responsible for maintaining the confidentiality of
              their login credentials.
            </p>

            <h4>6. Employee Rights</h4>
            <p>Employees may request:</p>
            <ul>
              <li>Access to their personal and work-related information</li>
              <li>Corrections or updates to their profile</li>
              <li>
                Deletion of certain data when appropriate and legally
                permissible
              </li>
            </ul>
            <p>
              Requests can be submitted through The Oppo's HR or IT departments.
            </p>

            <h4>7. Children's Privacy</h4>
            <p>
              The App is not intended for children and is used only by
              authorized employees.
            </p>

            <h4>8. Changes to This Privacy Policy</h4>
            <p>
              We may update this Privacy Policy from time to time. Changes will
              be posted on this page with an updated "Last Updated" date.
            </p>

            <h4>9. Contact Us</h4>
            <p>
              For questions about this Privacy Policy or data practices,
              contact:
            </p>
            <p class="contact-info">
              <strong>The Oppo</strong><br />
              Email:
              <a href="mailto:admin@alliessupportedemployment.com"
                >admin@alliessupportedemployment.com</a
              >
            </p>
          </div>
        </card>
      </div>
    </div>
  </auth-layout>
</template>

<script>
import AuthLayout from "./AuthLayout.vue";

export default {
  components: {
    AuthLayout,
  },
};
</script>

<style>
.privacy-page .card-header {
  background: none !important;
  height: 60px;
  font-family: "Playfair Display", serif;
}

.privacy-page .card {
  max-width: 900px !important;
  margin: 20px auto;
}
</style>

<style scoped>
.card-title {
  font-family: "Playfair Display", serif;
}

.card-body {
  padding: 30px 40px;
}

.privacy-content {
  color: #d9d9d9;
  font-family: "Nimbus Sans Light", sans-serif;
  font-size: 15px;
  line-height: 1.7;
  text-align: left;
}

.subtitle {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
}

.last-updated {
  font-size: 14px;
  font-style: italic;
  color: #b0b0b0;
  margin-bottom: 25px;
}

.privacy-content h4 {
  color: #fff;
  font-weight: bold;
  margin-top: 25px;
  margin-bottom: 15px;
  font-size: 17px;
}

.privacy-content h5 {
  color: #e6e6e6;
  font-weight: bold;
  margin-top: 15px;
  margin-bottom: 10px;
  font-size: 15px;
}

.privacy-content p {
  margin-bottom: 15px;
}

.privacy-content ul {
  margin-left: 20px;
  margin-bottom: 15px;
}

.privacy-content li {
  margin-bottom: 8px;
}

.privacy-content a {
  color: #66b3ff;
  text-decoration: none;
  transition: color 0.3s ease;
}

.privacy-content a:hover {
  color: #99ccff;
  text-decoration: underline;
}

.contact-info {
  background: rgba(255, 255, 255, 0.05);
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #66b3ff;
}

/* Mobile-specific adjustments */
@media (max-width: 768px) {
  .card-body {
    padding: 20px 15px;
  }

  .privacy-content {
    font-size: 14px;
  }

  .privacy-content h4 {
    font-size: 16px;
  }

  .privacy-content h5 {
    font-size: 14px;
  }
}
</style>
